/**
 * Slider controls, a carousel row's Scroll navigation (THEME.md §7.13, §9.4 #4, #9; product page spec R1–R5;
 * T-77; D-122, T-193; T-269): the element of `snippets/okt-slider-progress.liquid`. The token layer loads it on every page.
 *
 * - It controls the Horizon slideshow rendered just before it: the closest previous sibling that is, or
 *   directly holds, a `slideshow-component`.
 * - Or, with `data-row` (T-148), a scroll-snap row that isn't a slideshow: the element just before it when it
 *   matches that selector, else the element matching it inside, with the row's children as the cards (Judge.me's
 *   testimonials on the home page, `blocks/okt-review-controls.liquid`; the sub-collections; the product tabs' rows
 *   of pills, `blocks/okt-tabs.liquid`, the bar alone, D-122 CA3). Everything below works the same for both.
 * - The bar follows the scroll position one to one, with no transition. Its thumb is the visible part
 *   of the row (lists) or one slide (`data-thumb="slide"`, the product photos on phones).
 * - Each arrow click moves the row one card (T-118, as on fabini.cz; Horizon's own arrows page by the
 *   visible cards). The row scrolls to the next card's snap position and Horizon's slideshow follows, as
 *   it follows a swipe. The scroll takes the row's `scroll-behavior`, which Horizon sets to smooth, and
 *   to instant with reduced motion. At the start and the end the arrows get `aria-disabled="true"`
 *   instead of `disabled`, so keyboard focus stays on them.
 * - Scrolling, dragging and swiping stay Horizon's: every card is a snap point (`start`, normal
 *   snap-stop), so a slow scroll settles on the nearest card and a fast swipe can pass several.
 * - The row hides while the slides fit (nothing to scroll). A row's end padding counts as in view: cards or pills
 *   that reach only into it all show (the tab strip's room for focus rings, the review row's run-out).
 * - It rebinds when Horizon replaces the slideshow (variant changes, AJAX recommendations), and it
 *   re-applies its state when a morph resets its attributes.
 * - The thumb (T-269, PageSpeed fix 4): where the browser has scroll-driven animations with timeline scope (Chrome
 *   116+, Safari 26+), the browser moves it from the row's scroll position, with no script while the row moves: the
 *   row names a scroll timeline, the bar's parent puts it in scope, and the thumb runs a CSS animation along it
 *   (the snippet's rules, switched on by the attributes set here). The script measures the thumb's size and the
 *   cards' stops when the row changes size or cards, and sets the arrows' end states. Elsewhere it moves the thumb
 *   itself, at most once a frame and only while the row is on screen.
 * - Nothing forces a style or layout pass: the reading direction comes from `:dir()`, the first measure runs in the
 *   resize observer's callback (after layout), and each frame's bars are all read before any is written.
 */

/**
 * A row's size and cards, measured when they change: its scroll range, end padding, and the cards' stops
 * @typedef {{ scrollWidth: number, clientWidth: number, cards: number, range: number, padding: number, stops: number[] | null }} Geometry
 */

/** How long the last arrow click's card counts for the next click, so quick clicks add up (ms) */
const STEP_WINDOW = 1000;

/** Scroll-driven animations on a named timeline that a sibling can use (`timeline-scope`) */
const TIMELINE = CSS.supports('animation-timeline: --okt-progress') && CSS.supports('timeline-scope: --okt-progress');

class OktSliderProgress extends HTMLElement {
  /** Bars waiting for the next frame, with whether to measure their row's size and cards again */
  static #queued = /** @type {Map<OktSliderProgress, boolean>} */ (new Map());

  static #frame = 0;

  /** Each row and its bar */
  static #rows = /** @type {Map<Element, OktSliderProgress>} */ (new Map());

  /** One observer for every row: its first callback comes after the first layout, so reading sizes there is free */
  static #resizeObserver = new ResizeObserver((entries) => {
    const bars = new Set();
    for (const entry of entries) {
      const bar = OktSliderProgress.#rows.get(entry.target);
      if (bar) bars.add(bar);
    }
    OktSliderProgress.#run([...bars].map((bar) => /** @type {[OktSliderProgress, boolean]} */ ([bar, true])));
  });

  /**
   * Without the timeline: which rows are on screen, or within a screen of it, so a row away from it doesn't move its
   * thumb and one scrolling into view has caught up before it shows
   */
  static #visibility = TIMELINE
    ? null
    : new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const bar = OktSliderProgress.#rows.get(entry.target);
            if (!bar) continue;
            bar.#onScreen = entry.isIntersecting;
            if (entry.isIntersecting) bar.#queue();
          }
        },
        { rootMargin: '100% 0px' }
      );

  /**
   * Every bar reads, then every bar writes, so no read follows a write in the same frame
   * @param {Array<[OktSliderProgress, boolean]>} list
   */
  static #run(list) {
    const reads = list.map(([bar, geometry]) => bar.#measure(geometry));
    list.forEach(([bar], index) => bar.#apply(reads[index]));
  }

  static #flush() {
    OktSliderProgress.#frame = 0;
    const list = [...OktSliderProgress.#queued];
    OktSliderProgress.#queued.clear();
    // A bar whose slideshow was replaced binds the new one. A bar's first measure comes from the resize observer,
    // after layout, so a bar not measured yet waits for it here too
    OktSliderProgress.#run(list.filter(([bar]) => bar.isConnected && bar.#current() && bar.#geometry !== null));
  }

  /** @type {Element | null} */
  #slideshow = null;

  /** @type {HTMLElement | null} */
  #scroller = null;

  /** The bar's parent, which puts the row's scroll timeline in scope for the bar @type {Element | null} */
  #scope = null;

  /** The thumb follows the row's scroll timeline */
  #timeline = false;

  /** Without the timeline: the row is on screen (until the intersection observer says otherwise) */
  #onScreen = true;

  /** 1, or -1 when the row reads right to left */
  #direction = 1;

  /** @type {Geometry | null} */
  #geometry = null;

  /** What the last write left, so the self observer tells a morph's reset from this element's own writes */
  #written = { hidden: /** @type {boolean | null} */ (null), size: '', offset: '', ends: ['', ''] };

  /** The last arrow click: the position it left, its card and that card's position, valid until */
  #step = { from: 0, index: 0, to: 0, until: 0 };

  /** Replaced slideshows (parent), added, removed or hidden slides (scroller), and markers a morph dropped */
  #treeObserver = new MutationObserver((records) => {
    this.#mark();
    if (records.some((record) => record.type === 'childList' || record.attributeName === 'hidden')) this.#queue(true);
  });

  /** Morphs that reset the row's own state */
  #selfObserver = new MutationObserver(() => {
    this.#mark();
    if (!this.#unchanged()) this.#queue(true);
  });

  #onScroll = () => {
    if (this.#geometry && (this.#timeline || this.#onScreen)) this.#queue();
  };

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#selfObserver.observe(this, {
      attributes: true,
      attributeFilter: ['style', 'hidden', 'aria-disabled', 'data-okt-timeline'],
      subtree: true,
    });
    this.#bind(this.#find());
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#selfObserver.disconnect();
    this.#bind(null);
    OktSliderProgress.#queued.delete(this);
  }

  /**
   * The slideshow this row controls, or the scroll row itself with `data-row`
   * @returns {Element | null}
   */
  #find() {
    if (this.dataset.row) {
      const before = this.previousElementSibling;
      return before?.matches(this.dataset.row) ? before : before?.querySelector(this.dataset.row) ?? null;
    }

    for (let element = this.previousElementSibling; element; element = element.previousElementSibling) {
      if (element.localName === 'slideshow-component') return element;

      const inner = element.querySelector(':scope > slideshow-component');
      if (inner) return inner;
    }

    return null;
  }

  /**
   * Still bound to the slideshow before it; binds the new one otherwise
   * @returns {boolean}
   */
  #current() {
    const slideshow = this.#find();
    if (slideshow === this.#slideshow && this.#scroller?.isConnected) return true;

    this.#bind(slideshow);
    return false;
  }

  /**
   * @param {Element | null} slideshow
   */
  #bind(slideshow) {
    const previous = this.#scroller;
    if (previous) {
      previous.removeEventListener('scroll', this.#onScroll);
      OktSliderProgress.#resizeObserver.unobserve(previous);
      OktSliderProgress.#visibility?.unobserve(previous);
      if (OktSliderProgress.#rows.get(previous) === this) OktSliderProgress.#rows.delete(previous);
    }
    this.#treeObserver.disconnect();
    this.#unmark();

    this.#slideshow = slideshow;
    this.#scroller = this.dataset.row
      ? /** @type {HTMLElement | null} */ (slideshow)
      : slideshow?.querySelector(':scope > slideshow-container > slideshow-slides') ?? null;
    this.#direction = this.#rtl() ? -1 : 1;
    this.#geometry = null;
    this.#onScreen = true;

    const parent = this.parentElement;
    if (parent) {
      this.#treeObserver.observe(parent, { childList: true, attributes: true, attributeFilter: ['data-okt-progress-scope'] });
    }

    const scroller = this.#scroller;
    if (!scroller || !this.isConnected) {
      // Nothing to scroll: the row hides, as when its slides fit
      if (this.isConnected) {
        this.#written.hidden = true;
        if (!this.hidden) this.hidden = true;
      }
      return;
    }

    // The timeline needs the bar's parent to hold this row and no other bar: two rows of one name in one scope
    // leave both bars without a timeline
    this.#timeline =
      TIMELINE && !!parent && parent.contains(scroller) && parent.querySelectorAll(':scope > okt-slider-progress').length === 1;
    this.#scope = this.#timeline ? parent : null;
    this.#mark();

    scroller.addEventListener('scroll', this.#onScroll, { passive: true });
    OktSliderProgress.#rows.set(scroller, this);
    OktSliderProgress.#resizeObserver.observe(scroller);
    OktSliderProgress.#visibility?.observe(scroller);
    this.#treeObserver.observe(scroller, {
      childList: true,
      attributes: true,
      attributeFilter: ['hidden', 'data-okt-progress-row'],
      subtree: true,
    });
  }

  /** Right to left, from the document's `dir` (`:dir()` reads no style) */
  #rtl() {
    try {
      return this.matches(':dir(rtl)');
    } catch {
      return document.documentElement.dir === 'rtl';
    }
  }

  /** The timeline's markers (the snippet's rules): the row names it, the parent scopes it, the bar's thumb follows it */
  #mark() {
    const scroller = this.#scroller;
    const scope = this.#scope;
    if (!this.#timeline || !scroller || !scope) return;

    if (!scroller.hasAttribute('data-okt-progress-row')) scroller.setAttribute('data-okt-progress-row', '');
    if (!scope.hasAttribute('data-okt-progress-scope')) scope.setAttribute('data-okt-progress-scope', '');
    if (!this.hasAttribute('data-okt-timeline')) this.setAttribute('data-okt-timeline', '');
    if (this.style.getPropertyValue('--okt-progress-offset')) this.style.removeProperty('--okt-progress-offset');
  }

  #unmark() {
    if (!this.#timeline) return;

    this.#scroller?.removeAttribute('data-okt-progress-row');
    this.#scope?.removeAttribute('data-okt-progress-scope');
    this.removeAttribute('data-okt-timeline');
    this.#timeline = false;
    this.#scope = null;
  }

  /** @param {boolean} [geometry] Measure the row's size and cards again too */
  #queue(geometry = false) {
    const queued = OktSliderProgress.#queued;
    queued.set(this, queued.get(this) || geometry);
    if (!OktSliderProgress.#frame) OktSliderProgress.#frame = requestAnimationFrame(OktSliderProgress.#flush);
  }

  /** The cards whose stops the arrows step through */
  #cards() {
    const scroller = this.#scroller;
    if (!scroller) return [];
    return this.dataset.row ? [...scroller.children] : [...scroller.querySelectorAll(':scope > slideshow-slide')];
  }

  /**
   * Each shown card's snap position: its distance from the first card along the reading direction
   * @returns {number[]}
   */
  #measureStops() {
    const slides = this.#cards().filter((slide) => slide instanceof HTMLElement && slide.offsetWidth > 0);
    if (slides.length < 2) return [];

    const first = slides[0].getBoundingClientRect().left;
    return slides.map((slide) => (slide.getBoundingClientRect().left - first) * this.#direction);
  }

  /**
   * Reads only: the row's position, and its size and cards when they changed (or `geometry`)
   * @param {boolean} geometry
   */
  #measure(geometry) {
    const scroller = this.#scroller;
    if (!scroller?.isConnected) return null;

    const scrollWidth = scroller.scrollWidth;
    const clientWidth = scroller.clientWidth;
    let measured = this.#geometry;
    if (geometry || !measured || measured.scrollWidth !== scrollWidth || measured.clientWidth !== clientWidth) {
      measured = {
        scrollWidth,
        clientWidth,
        cards: this.#cards().length,
        range: scrollWidth - clientWidth,
        // A row's end padding is part of its scroll range, but what reaches only into it is in view
        padding: this.dataset.row ? parseFloat(getComputedStyle(scroller).paddingInlineEnd) || 0 : 0,
        // The stops, for rows with arrows: measured with the row, not when an arrow is pressed
        stops: this.querySelector('button[data-action]') ? this.#measureStops() : null,
      };
    }

    const { range } = measured;
    const hidden = range < 1 + measured.padding;

    // Within a pixel of either end counts as the end: scroll positions can stop at a fraction short of it
    let travelled = Math.min(Math.abs(scroller.scrollLeft), range);
    if (travelled < 1) travelled = 0;
    if (range - travelled < 1) travelled = range;

    let size = clientWidth / scrollWidth;
    if (this.dataset.thumb === 'slide') {
      const count = scroller.querySelectorAll(':scope > slideshow-slide:not([hidden]), :scope > slideshow-slide[reveal]').length;
      size = 1 / Math.max(count, 1);
    }

    return { measured, hidden, travelled, range, size };
  }

  /**
   * Writes only, and only what changed
   * @param {{ measured: Geometry, hidden: boolean, travelled: number, range: number, size: number } | null} read
   */
  #apply(read) {
    if (!read) return;

    this.#geometry = read.measured;
    if (this.hidden !== read.hidden) this.hidden = read.hidden;
    this.#written.hidden = read.hidden;
    if (read.hidden) return;

    const { travelled, range, size } = read;
    this.#setProperty('--okt-progress-size', size.toFixed(4));

    // Without the timeline: the thumb moves across the rest of the track; translate() percentages are of the thumb
    if (!this.#timeline) {
      const offset = (travelled / range) * (1 / size - 1) * 100 * this.#direction;
      this.#setProperty('--okt-progress-offset', `${offset.toFixed(3)}%`);
    }

    for (const button of this.querySelectorAll('button[data-action]')) {
      const previous = button.getAttribute('data-action') === 'previous';
      const value = String(previous ? travelled <= 1 : travelled >= range - 1);
      if (button.getAttribute('aria-disabled') !== value) button.setAttribute('aria-disabled', value);
      this.#written.ends[previous ? 0 : 1] = value;
    }
  }

  /**
   * Writes a custom property only when it changes, so the self observer settles
   * @param {string} name
   * @param {string} value
   */
  #setProperty(name, value) {
    if (this.style.getPropertyValue(name) !== value) this.style.setProperty(name, value);
    if (name === '--okt-progress-size') this.#written.size = value;
    else this.#written.offset = value;
  }

  /**
   * The row's own state is as this element last wrote it: the self observer saw its own writes (its markers
   * included), not a morph's reset. Before the first measure there is nothing to restore: that measure is on its way.
   */
  #unchanged() {
    const written = this.#written;
    if (written.hidden === null) return true;
    if (this.hidden !== written.hidden) return false;
    if (written.hidden) return true;
    if (this.style.getPropertyValue('--okt-progress-size') !== written.size) return false;
    if (!this.#timeline && this.style.getPropertyValue('--okt-progress-offset') !== written.offset) return false;

    for (const button of this.querySelectorAll('button[data-action]')) {
      const end = written.ends[button.getAttribute('data-action') === 'previous' ? 0 : 1];
      if (button.getAttribute('aria-disabled') !== end) return false;
    }
    return true;
  }

  /**
   * One card per click: from the card at the row's start edge (or the last click's card while its
   * scroll runs) to its neighbour, stopping at the card that sits at the start edge once the row has
   * scrolled to its end.
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('button[data-action]') : null;
    if (!button || !this.contains(button) || button.getAttribute('aria-disabled') === 'true') return;

    const scroller = this.#scroller;
    if (!scroller) return;

    // The stops measured with the row; again here if the row or its cards changed since
    const range = scroller.scrollWidth - scroller.clientWidth;
    const measured = this.#geometry;
    const stops =
      measured?.stops &&
      measured.scrollWidth === scroller.scrollWidth &&
      measured.clientWidth === scroller.clientWidth &&
      measured.cards === this.#cards().length
        ? measured.stops
        : this.#measureStops();
    if (stops.length < 2) return;

    let last = stops.findIndex((stop) => stop >= range - 1);
    if (last < 0) last = stops.length - 1;

    const travelled = Math.abs(scroller.scrollLeft);
    let current = 0;
    stops.forEach((stop, index) => {
      if (Math.abs(stop - travelled) < Math.abs(stops[current] - travelled)) current = index;
    });

    // A quick click while the last click's scroll is still on its way counts from that click's card
    const { from, to, until } = this.#step;
    if (performance.now() < until && travelled >= Math.min(from, to) - 1 && travelled <= Math.max(from, to) + 1) {
      current = this.#step.index;
    }

    const index = Math.min(Math.max(current + (button.getAttribute('data-action') === 'previous' ? -1 : 1), 0), last);
    const left = Math.min(stops[index], range);
    this.#step = { from: travelled, index, to: left, until: performance.now() + STEP_WINDOW };
    scroller.scrollTo({ left: left * this.#direction });
  };
}

if (!customElements.get('okt-slider-progress')) {
  customElements.define('okt-slider-progress', OktSliderProgress);
}
